/**
 * Subscription Service
 * LessonlyAI - Manages user subscription data
 */

import { supabase } from './supabase';
import type {
  Subscription,
  SubscriptionTier,
  SubscriptionStatus,
  UserSubscriptionDetails,
} from '../types/subscription';
import { SubscriptionNotFoundError, normalizeTier } from '../types/subscription';
import { singleFlight } from '../src/utils/singleFlight';

/**
 * Get user's subscription from database
 * @param userId - User ID
 * @returns Subscription record
 * @throws SubscriptionNotFoundError if subscription doesn't exist
 */
export async function getUserSubscription(
  userId: string
): Promise<Subscription> {
  // Wrap in single-flight to prevent duplicate concurrent calls
  return singleFlight(`subscription:${userId}`, async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error fetching subscription:', error);

        // If subscription doesn't exist, try to create one
        if (error.code === 'PGRST116') {
          return await createFreeSubscription(userId);
        }

        throw new Error(`Failed to get subscription: ${error.message}`);
      }

      return data as Subscription;
    } catch (error) {
      console.error('Exception in getUserSubscription:', error);
      throw error;
    }
  });
}

/**
 * Get user's subscription tier
 * @param userId - User ID
 * @returns Subscription tier (free, essential, pro)
 */
export async function getUserTier(userId: string): Promise<SubscriptionTier> {
  try {
    const subscription = await getUserSubscription(userId);
    // Normalize tier for backwards compatibility during migration
    return normalizeTier(subscription.tier);
  } catch (error) {
    console.error('Exception in getUserTier:', error);
    // Default to free tier on error
    return 'free';
  }
}

/**
 * Get user's subscription status
 * @param userId - User ID
 * @returns Subscription status (active, canceled, etc.)
 */
export async function getUserSubscriptionStatus(
  userId: string
): Promise<SubscriptionStatus> {
  try {
    const subscription = await getUserSubscription(userId);
    return subscription.status;
  } catch (error) {
    console.error('Exception in getUserSubscriptionStatus:', error);
    return 'active';
  }
}

/**
 * Check if user has an active subscription
 * @param userId - User ID
 * @returns true if subscription is active or trialing
 */
export async function hasActiveSubscription(userId: string): Promise<boolean> {
  try {
    const status = await getUserSubscriptionStatus(userId);
    return status === 'active' || status === 'trialing';
  } catch (error) {
    console.error('Exception in hasActiveSubscription:', error);
    return false;
  }
}

/**
 * Check if user is on free tier
 * @param userId - User ID
 * @returns true if user is on free tier
 */
export async function isFreeTier(userId: string): Promise<boolean> {
  try {
    const tier = await getUserTier(userId);
    return tier === 'free';
  } catch (error) {
    console.error('Exception in isFreeTier:', error);
    return true;
  }
}

/**
 * Check if user is on paid tier (essential or pro)
 * @param userId - User ID
 * @returns true if user has a paid subscription
 */
export async function isPaidTier(userId: string): Promise<boolean> {
  try {
    const tier = await getUserTier(userId);
    return tier === 'essential' || tier === 'pro';
  } catch (error) {
    console.error('Exception in isPaidTier:', error);
    return false;
  }
}

/**
 * Get or create user subscription (calls database function)
 * @param userId - User ID
 * @returns Subscription record
 */
export async function getOrCreateSubscription(
  userId: string
): Promise<Subscription> {
  try {
    const { data, error } = await supabase.rpc('get_or_create_subscription', {
      p_user_id: userId,
    });

    if (error) {
      console.error('Error in get_or_create_subscription:', error);
      throw new Error(`Failed to get/create subscription: ${error.message}`);
    }

    // RPC returns array, get first element
    const subscription = Array.isArray(data) ? data[0] : data;

    if (!subscription) {
      throw new SubscriptionNotFoundError(userId);
    }

    return subscription as Subscription;
  } catch (error) {
    console.error('Exception in getOrCreateSubscription:', error);
    throw error;
  }
}

/**
 * Create a free tier subscription for new user
 * @param userId - User ID
 * @returns Created subscription
 */
async function createFreeSubscription(userId: string): Promise<Subscription> {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .insert({
        user_id: userId,
        tier: 'free',
        status: 'active',
        cancel_at_period_end: false,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating free subscription:', error);
      throw new Error(`Failed to create subscription: ${error.message}`);
    }

    return data as Subscription;
  } catch (error) {
    console.error('Exception in createFreeSubscription:', error);
    throw error;
  }
}

/**
 * Get comprehensive user subscription details
 * Includes subscription info + current month usage
 * @param userId - User ID
 * @returns User subscription details with usage stats
 */
export async function getUserSubscriptionDetails(
  userId: string
): Promise<UserSubscriptionDetails | null> {
  try {
    // Check if RPC function exists, otherwise build manually
    const { data, error } = await supabase.rpc(
      'get_user_subscription_details',
      {
        p_user_id: userId,
      }
    );

    if (error) {
      console.error('Error fetching subscription details:', error);

      // If RPC doesn't exist, build details manually
      if (error.code === '42883') {
        // Function doesn't exist
        return await buildSubscriptionDetailsManually(userId);
      }

      throw new Error(`Failed to get subscription details: ${error.message}`);
    }

    return data as UserSubscriptionDetails;
  } catch (error) {
    console.error('Exception in getUserSubscriptionDetails:', error);
    // Try fallback
    return await buildSubscriptionDetailsManually(userId);
  }
}

/**
 * Build subscription details manually by querying tables
 * Fallback when RPC function isn't available
 * @param userId - User ID
 * @returns User subscription details
 */
async function buildSubscriptionDetailsManually(
  userId: string
): Promise<UserSubscriptionDetails | null> {
  try {
    const subscription = await getUserSubscription(userId);

    // Import to avoid circular dependency
    const { getCurrentMonthYear, getTierLimit } = await import('../types/subscription');
    const monthYear = getCurrentMonthYear();

    // Get current month usage
    const { data: usageData } = await supabase
      .from('usage_tracking')
      .select('lessons_generated')
      .eq('user_id', userId)
      .eq('month_year', monthYear)
      .single();

    const used = usageData?.lessons_generated || 0;
    const normalizedTier = normalizeTier(subscription.tier);
    const limit = getTierLimit(normalizedTier);
    const remaining = Math.max(0, limit - used);

    return {
      user_id: subscription.user_id,
      subscription_id: subscription.id,
      tier: normalizedTier,
      status: subscription.status,
      stripe_customer_id: subscription.stripe_customer_id,
      stripe_subscription_id: subscription.stripe_subscription_id,
      current_period_start: subscription.current_period_start,
      current_period_end: subscription.current_period_end,
      cancel_at_period_end: subscription.cancel_at_period_end,
      trial_end: subscription.trial_end,
      lessons_generated: used,
      month_year: monthYear,
      monthly_limit: limit,
      lessons_remaining: remaining,
    };
  } catch (error) {
    console.error('Exception in buildSubscriptionDetailsManually:', error);
    return null;
  }
}

/**
 * Check if user's subscription will cancel at period end
 * @param userId - User ID
 * @returns true if subscription is set to cancel
 */
export async function willCancelAtPeriodEnd(userId: string): Promise<boolean> {
  try {
    const subscription = await getUserSubscription(userId);
    return subscription.cancel_at_period_end;
  } catch (error) {
    console.error('Exception in willCancelAtPeriodEnd:', error);
    return false;
  }
}

/**
 * Get subscription period end date
 * @param userId - User ID
 * @returns Period end date or null
 */
export async function getSubscriptionPeriodEnd(
  userId: string
): Promise<Date | null> {
  try {
    const subscription = await getUserSubscription(userId);
    if (!subscription.current_period_end) return null;
    return new Date(subscription.current_period_end);
  } catch (error) {
    console.error('Exception in getSubscriptionPeriodEnd:', error);
    return null;
  }
}
